import { useEffect, useState } from "react";
import { Tag, Timer, X } from "lucide-react";
import { getBanner } from "@/lib/content";

const STORAGE_KEY = "bpp_promo_dismissed_v2";

const DEFAULT_BANNER = {
  active: true,
  label: "Monsoon Special",
  headline: "Save up to 25% on Exterior Painting. Book by Dec 31.",
  cta_label: "Claim offer",
  end_at: "2026-12-31T23:59:59+05:30",
};

const pad = (n) => String(n).padStart(2, "0");

function useCountdown(endAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endAt]);
  if (!endAt) return null;
  const end = new Date(endAt).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - now;
  if (diff <= 0) return { expired: true };
  const s = Math.floor(diff / 1000);
  return {
    expired: false,
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    sec: s % 60,
  };
}

export default function PromoBanner({ onCtaClick }) {
  const [show, setShow] = useState(false);
  const [banner, setBanner] = useState(DEFAULT_BANNER);
  const countdown = useCountdown(banner.end_at);

  useEffect(() => {
    getBanner()
      .then((b) => setBanner({ ...DEFAULT_BANNER, ...b }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed && banner.active) setShow(true);
      else setShow(false);
    } catch {
      setShow(banner.active);
    }
  }, [banner.active]);

  if (!show || !banner.active || countdown?.expired) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    setShow(false);
  };

  return (
    <div data-testid="promo-banner" className="relative z-40 bg-ink text-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-11 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[13px] font-semibold truncate">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/90 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
            <Tag className="h-3 w-3" /> {banner.label}
          </span>
          <span className="truncate">{banner.headline}</span>
          {countdown && !countdown.expired && (
            <>
              <span
                data-testid="promo-countdown"
                className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-bold tabular-nums text-orange-200"
              >
                <Timer className="h-3 w-3" />
                Ends in {countdown.d}d {pad(countdown.h)}:{pad(countdown.m)}:{pad(countdown.sec)}
              </span>
              <span
                data-testid="promo-countdown-mobile"
                className="md:hidden inline-flex shrink-0 items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold tabular-nums text-orange-200"
              >
                <Timer className="h-3 w-3" />
                {countdown.d}d {pad(countdown.h)}h left
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={onCtaClick}
            className="hidden sm:inline-flex text-[12px] font-bold text-orange-300 hover:text-orange-200"
            data-testid="promo-cta"
          >
            {banner.cta_label} →
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss promo"
            data-testid="promo-dismiss"
            className="h-7 w-7 rounded-full grid place-items-center hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
